/**
 * @file actions.ts - Действия для модального окна бронирования
 * @todo - декомпозировать на несколько файлов
 * @todo - совместить логику remarked и restoplace
 * @description
 *    - submitBooking - Отправка бронирования!
 *    - handleSubmitForm - Проверяем валидность всех полей формы, отправляем форму
 *    - checkDiscountByNumber - Проверяем скидку по телефону
 *    - remarkedBookingHandler - Remarked, обработка бронирования для платформы
 *    - restoplaceBookingHandler - Restoplace, обработка бронирования для платформы
 *    - openBooking - Открываем модальное окно бронирования
 */

import { TypeExtendedScheduleDay, TypeExtendedScheduleTimeSlot } from '@shared/types/schedule/types'
import { TypeMappingRestOutput } from '@shared/api/middleware/mappingAPI/restaurant/type'
import { TypeOpenBookingInitial, TypeBookingHandlerParams, TypeUpdateTimeSlotsParams } from './types'

import { AnyAction, createAsyncThunk } from '@reduxjs/toolkit'
import { Dispatch } from 'redux'

import { globalStore } from '@app/model/store'

import {
  openModal,
  setCheckDiscountLoading,
  setDatesOptions,
  setDisabledAlertItem,
  setGuestsCountValue,
  setIsErrorsVisible,
  setIsOpenScreenSuccess,
  setNameFieldValue,
  setPhoneFieldValue,
  setRestaurantData,
  setSelectedDate,
  setSelectedTime,
  setTimesOptions,
} from '@widgets/Restaurant/Booking/model/slice'

import { addTemporaryNotice } from '@widgets/Common/Notices'
import { fetchRemarkedSlots, initFetchRemarkedSlotsNearTwoDays } from '@widgets/Restaurant/Booking/model/thunks'
import { fetchRestoplaceSlots, initFetchRestoplaceSlotsNearTwoDays } from '@widgets/Restaurant/Booking/model/thunks'
import { isPhonePatternValid } from '@widgets/Restaurant/Booking/lib/helper'

import { API } from '@shared/api'
import { EGG } from '@shared/api/analytics'
import { generateDatesOptions } from '@shared/lib/helpers/schedule/schedule'

const isDevParam = typeof window === 'object' && window.location.search.indexOf('is_dev=true') > 0

/**
 * @function submitBooking - Отправка бронирования!
 */
export const submitBooking = createAsyncThunk('booking/submitBooking', async (action, { dispatch, getState }) => {
  // @ts-ignore
  const { form, restaurantSource } = getState().booking

  const { guestsCountField, nameField, phoneField, commentField, dateField, timeField } = form

  // для проверки валидности всех полей формы
  const arrayValidFields = [
    guestsCountField.valid,
    nameField.valid,
    phoneField.valid,
    commentField.valid,
    dateField.valid,
    timeField.valid,
  ]

  const isValidAllFields = arrayValidFields.every((fieldValid) => fieldValid)
  if (!isValidAllFields) {
    // @ts-ignore
    dispatch(setIsErrorsVisible())
    return
  }

  EGG.entity.booking.click_reserve_book({
    count_guest: String(guestsCountField.value),
    phone: phoneField.value,
    date: String(dateField.selected.value),
    time: String(timeField.selected.value),
  })

  const date = dateField.selected.value
  const time = timeField.selected.value

  // отправляем бронирование на сервер
  API.booking_create({
    body: {
      restaurant: Number(restaurantSource.id),
      guest_phone: phoneField.value,
      num_of_places: Number(guestsCountField.value),
      reservation_at: `${date} ${time}`,
      guest_name: nameField.value,
      comment: commentField.value,
    },
    // repeated: true,
  })
    .then(() => {
      // открываем модалку об успешном бронировании
      dispatch(setIsOpenScreenSuccess(true))

      // сохраняем имени и телефона в localStorage
      if (window?.localStorage && nameField?.value && phoneField?.value) {
        window.localStorage.setItem('booking-guest-name', JSON.stringify(nameField.value))
        window.localStorage.setItem('booking-guest-phone', JSON.stringify(phoneField.value))
      }
    })
    .catch((error) => {
      const noticeText = error?.message || 'Ошибка отправки формы бронирования'
      dispatch(addTemporaryNotice({ markup: noticeText }))
    })
})

/**
 * @function handleSubmitForm - Проверяем валидность всех полей формы, отправляем форму
 */
export const handleSubmitForm = () => (dispatch: Dispatch<AnyAction>) => {
  // @ts-ignore
  dispatch(setIsErrorsVisible())
}

/**
 * @function checkDiscountByNumber - Проверяем скидку по телефону
 */
export const checkDiscountByNumber = createAsyncThunk(
  'booking/checkDiscountByNumber',
  async (action: { phone: string; restId: number }, { dispatch, getState }) => {
    // @ts-ignore
    const stateDiscounts = getState().booking.restaurantSource.discounts
    const hasDiscounts = !!stateDiscounts.tastyclub || !!stateDiscounts.remarked

    if (!hasDiscounts) return
    if (!isPhonePatternValid(action.phone)) return

    const numbers = parseInt(action.phone.replace(/[^\d]/g, ''))

    dispatch(setCheckDiscountLoading(true))
    dispatch(setDisabledAlertItem({ type: 'discount', disabled: true }))

    await API.booking_discount({ body: { phone: numbers, rest_id: action.restId } })
      .then((res) => {
        dispatch(setDisabledAlertItem({ type: 'discount', disabled: !res.discount }))
      })
      .finally(() => {
        dispatch(setCheckDiscountLoading(false))
      })
  },
)

/**
 * @function remarkedBookingHandler - Remarked, обработка бронирования для платформы
 */
const remarkedBookingHandler = {
  init: async (params: TypeBookingHandlerParams) => {
    const { dispatch, initialGuests, restSlug, schedule, initialDate, initialTime } = params

    // Делаем запрос, чтобы отображать кнопки "сегодня", "завтра" или нет.
    // - Если слоты будут, то отображаются кнопки
    // - Если слотов нет, то отображается только календарь, и в слотах будет надпись "Выберите дату"
    await dispatch(
      // @ts-ignore
      initFetchRemarkedSlotsNearTwoDays({
        guests: initialGuests,
        restSlug,
        schedule,
      }),
    )

    if (initialDate) {
      // Если передан параметр на предустановленный слот,
      // делаем еще один запрос на данный слот (с учетом кол-ва гостей).
      // Это нужно, чтобы получить список всех доступных слотов на выбранный день

      await dispatch(
        // @ts-ignore
        fetchRemarkedSlots({
          guests: initialGuests,
          restSlug,
          date: initialDate,
        }),
      )

      dispatch(setSelectedTime(initialTime))
    }
  },

  // обновляем слоты для выбранного дня
  updateTimeSlots: async (params: TypeUpdateTimeSlotsParams) => {
    const { dispatch, guests, restSlug, date } = params

    await dispatch(
      // @ts-ignore
      fetchRemarkedSlots({
        guests,
        restSlug,
        date,
      }),
    )
  },
}

/**
 * @function restoplaceBookingHandler - Restoplace, обработка бронирования для платформы
 */
const restoplaceBookingHandler = {
  init: async (params: TypeBookingHandlerParams) => {
    const { dispatch, initialGuests, restSlug, schedule, initialDate, initialTime } = params

    // инициализация слотов для ближайших двух дней
    await dispatch(
      // @ts-ignore
      initFetchRestoplaceSlotsNearTwoDays({
        guests: initialGuests,
        restSlug,
        schedule,
      }),
    )

    if (initialDate) {
      await dispatch(
        // @ts-ignore
        fetchRestoplaceSlots({
          guests: initialGuests,
          restSlug,
          date: initialDate,
        }),
      )

      dispatch(setSelectedTime(initialTime))
    }
  },

  // обновляем слоты для выбранного дня
  updateTimeSlots: async (params: TypeUpdateTimeSlotsParams) => {
    const { dispatch, guests, restSlug, date } = params

    await dispatch(
      // @ts-ignore
      fetchRestoplaceSlots({
        guests,
        restSlug,
        date,
      }),
    )
  },
}

/**
 * @function openBooking - Открываем модальное окно бронирования
 */
export const openBooking =
  (restaurantData: TypeMappingRestOutput, initial?: TypeOpenBookingInitial) =>
  async (dispatch: Dispatch<AnyAction>) => {
    const {
      date: initialDate,
      time: initialTime,
      guests: initialGuests,
    } = { date: null, time: null, guests: 2, ...initial }

    dispatch(setRestaurantData(restaurantData))
    dispatch(setGuestsCountValue(initialGuests))
    dispatch(openModal(null))
    // @ts-ignore
    dispatch(setInitialFieldsValues())

    // определяем тип платформы
    const getHandlerType = (data: TypeMappingRestOutput) => {
      const states: Record<string, () => boolean> = {
        restoplace: () => Boolean(data?.booking?.afisha?.restoplace?.active),
        remarked: () => Boolean(data?.booking?.afisha?.remarked?.active),
        default: () => true,
      }

      // если платформа не определена, то default
      return Object.entries(states).find(([_, condition]) => condition())?.[0] || 'default'
    }

    // Обработчики
    const bookingHandlers = {
      restoplace: async () => {
        isDevParam && console.log('!!! restoplaceBookingHandler.init')

        await restoplaceBookingHandler.init({
          dispatch,
          initialGuests,
          restSlug: restaurantData.url.slug,
          schedule: restaurantData.schedule.week,
          initialDate,
          initialTime,
        })
      },

      remarked: async () => {
        isDevParam && console.log('!!! remarkedBookingHandler.init')

        await remarkedBookingHandler.init({
          dispatch,
          initialGuests,
          restSlug: restaurantData.url.slug,
          schedule: restaurantData.schedule.week,
          initialDate,
          initialTime,
        })
      },

      default: async () => {
        isDevParam && console.log('!!! setTimesOptions')

        dispatch(setDatesOptions(null))
        dispatch(setSelectedDate(initialDate))
        dispatch(setTimesOptions(null))
        dispatch(setSelectedTime(initialTime))
      },
    }

    const handlerType = getHandlerType(restaurantData)
    await bookingHandlers[handlerType]()
  }

/**
 * @function setInitialFieldsValues - Устанавливаем начальные значения полей
 */
export const setInitialFieldsValues = () => (dispatch: Dispatch<AnyAction>) => {
  try {
    const userData = globalStore.getState().global.user.data

    const localStorageName = JSON.parse(window?.localStorage.getItem('booking-guest-name'))
    const localStoragePhone = JSON.parse(window?.localStorage.getItem('booking-guest-phone'))

    const userDataFullName = userData ? `${userData.first_name} ${userData.last_name}` : null

    const name = userDataFullName || localStorageName || null

    // @ts-ignore
    const phone = userData?.phone || localStoragePhone || null

    name && dispatch(setNameFieldValue(name))
    phone && dispatch(setPhoneFieldValue(phone))
  } catch (e) {
    console.error(e)
  }
}

/**
 * @function updateTimeSlotsFromBehindDateAC - Обновление слотов при изменении даты
 */
export const updateTimeSlotsFromBehindDateAC =
  (date: TypeExtendedScheduleDay) => async (dispatch: Dispatch<AnyAction>) => {
    const stateBooking = globalStore.getState().booking
    const { booking, slug } = stateBooking.restaurantSource

    if (!booking?.afisha) {
      console.error('booking afisha data is not available')
      return
    }

    // определяем тип платформы
    const getHandlerType = (data: any) => {
      const states: Record<string, () => boolean> = {
        remarked: () => Boolean(data?.booking?.afisha?.remarked?.active),
        restoplace: () => Boolean(data?.booking?.afisha?.restoplace?.active),
        default: () => true,
      }

      return Object.entries(states).find(([_, condition]) => condition())?.[0] || 'default'
    }

    // обработчики
    const timeSlotsHandlers = {
      remarked: async () => {
        isDevParam && console.log('!!! remarkedBookingHandler.updateTimeSlots')
        await remarkedBookingHandler.updateTimeSlots({
          dispatch,
          guests: stateBooking.form.guestsCountField.value,
          restSlug: slug,
          date,
        })
      },

      restoplace: async () => {
        isDevParam && console.log('!!! restoplaceBookingHandler.updateTimeSlots')
        await restoplaceBookingHandler.updateTimeSlots({
          dispatch,
          guests: stateBooking.form.guestsCountField.value,
          restSlug: slug,
          date,
        })
      },

      default: () => {
        isDevParam && console.log('!!! setTimesOptions')
        dispatch(setTimesOptions(date))
      },
    }

    const handlerType = getHandlerType(booking)
    await timeSlotsHandlers[handlerType]()
  }

/**
 * @function updateTimeSlotsFromBehindGuestsAC - Обновление слотов при изменении гостей
 */
export const updateTimeSlotsFromBehindGuestsAC = () => async (dispatch: Dispatch<AnyAction>) => {
  const stateBooking = globalStore.getState().booking
  const restData = stateBooking.restaurantSource
  const formattedDate = stateBooking.form.dateField.selected

  if (!restData?.booking?.afisha) {
    console.error('booking afisha data is not available')
    return
  }

  if (restData?.booking?.afisha?.restoplace?.active) {
    await restoplaceBookingHandler.updateTimeSlots({
      dispatch,
      guests: stateBooking.form.guestsCountField.value,
      restSlug: restData.slug,
      date: formattedDate,
    })
  } else if (restData?.booking?.afisha?.remarked?.active) {
    await remarkedBookingHandler.updateTimeSlots({
      dispatch,
      guests: stateBooking.form.guestsCountField.value,
      restSlug: restData.slug,
      date: formattedDate,
    })
  }
}

/**
 * @function setGuestsAC - Счетчик гостей. Установка нового кол-ва гостей
 */
export const setGuestsAC = (value: number) => (dispatch: Dispatch<AnyAction>) => {
  dispatch(setGuestsCountValue(value))
  // @ts-ignore
  dispatch(updateTimeSlotsFromBehindGuestsAC())
}

/**
 * @function setDateAC - Слоты времени. Установка нового выбранного дня.
 */
export const setDateAC = (date: Date) => (dispatch: Dispatch<AnyAction>) => {
  const stateBooking = globalStore.getState().booking
  const restData = stateBooking.restaurantSource
  if (!restData?.booking?.afisha) {
    console.error('booking data is not available')
    return
  }

  console.log('изменение даты в календаре:', {
    новаяДата: date.toISOString().split('T')[0],
    типПлатформы: restData?.booking?.afisha?.remarked?.active ? 'remarked' : 'restoplace',
    текущаяДата: stateBooking.form.dateField.selected?.value,
  })

  dispatch(setSelectedTime(null))

  // Если это remarked слот, используем дату напрямую
  if (restData?.booking?.afisha?.remarked?.active) {
    const formattedDateResult = {
      itemText: date.toLocaleDateString(),
      itemTextShort: date.toLocaleDateString(),
      schedule: null,
      openRestDate: date.toISOString().split('T')[0],
      value: date.toISOString().split('T')[0],
      thisDate: date.toISOString().split('T')[0],
      isRestOpened: true,
    }

    console.log('форматированная дата для remarked:', formattedDateResult)

    dispatch(setSelectedDate(formattedDateResult))
    // @ts-ignore
    dispatch(updateTimeSlotsFromBehindDateAC(formattedDateResult))
    // @ts-ignore
    dispatch(updateTimeSlotsFromBehindGuestsAC())
  } else {
    const formattedDateResult = generateDatesOptions(restData.schedule, { preferredDay: date })[0]

    console.log('форматированная дата для restoplace:', formattedDateResult)

    dispatch(setSelectedDate(formattedDateResult))
    // @ts-ignore
    dispatch(updateTimeSlotsFromBehindDateAC(formattedDateResult))
    // @ts-ignore
    dispatch(updateTimeSlotsFromBehindGuestsAC())
  }
}
